/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import {
  RouteDefinition,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";

export type AdminPrimarySection =
  | "dashboard"
  | "accounts"
  | "activity"
  | "conversion"
  | "my-account";

export function AdminNavigation({
  current,
  routeDashboard,
  routeAccounts,
  routeActivity,
  routeConversion,
  routeMyAccount,
  showConversion,
}: {
  current?: AdminPrimarySection;
  routeDashboard: RouteDefinition;
  routeAccounts: RouteDefinition;
  routeActivity: RouteDefinition;
  routeConversion: RouteDefinition;
  routeMyAccount: RouteDefinition;
  showConversion: boolean;
}): VNode {
  const { i18n } = useTranslationContext();
  const links = [
    {
      id: "dashboard" as const,
      label: i18n.str`Dashboard`,
      route: routeDashboard,
    },
    {
      id: "accounts" as const,
      label: i18n.str`Accounts`,
      route: routeAccounts,
    },
    {
      id: "activity" as const,
      label: i18n.str`Admin activity`,
      route: routeActivity,
    },
    ...(showConversion
      ? [
          {
            id: "conversion" as const,
            label: i18n.str`Conversion`,
            route: routeConversion,
          },
        ]
      : []),
    {
      id: "my-account" as const,
      label: i18n.str`My account`,
      route: routeMyAccount,
    },
  ];

  return (
    <nav
      aria-label={i18n.str`Bank administration`}
      class="mb-6 overflow-x-auto border-b border-outlineVariant"
    >
      <ul class="flex min-w-max gap-6">
        {links.map((link) => {
          const selected = current === link.id;
          return (
            <li key={link.id}>
              <a
                href={link.route.url({})}
                aria-current={selected ? "page" : undefined}
                data-selected={selected}
                class="block border-b-2 border-transparent px-1 pb-3 text-sm font-semibold text-secondary hover:border-primary/40 hover:text-primaryDark data-[selected=true]:border-primary data-[selected=true]:text-primaryDark"
              >
                {link.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
